import { PageController } from '@client/components/_core/layout/PageController';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import type { FileRouteTypes } from '@client/routeTree.gen';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Typography } from 'antd';
import { Bot, FileText, LayoutGrid, PenLine, Table2 } from 'lucide-react';

const { Title, Paragraph } = Typography;

export const Route = createFileRoute('/console/$orgId/component-showcase/')({
    staticData: {
        breadcrumb: { title: 'Component Showcase' },
        featureFlag: 'enable-debug-info',
    },
    component: ComponentShowcaseIndex,
});

function ComponentShowcaseIndex() {
    const orgId = useOrgId();
    const navigate = useNavigate();

    const showcasePages = [
        {
            title: 'Table component showcase',
            description: 'Interactive demonstration of PrismaTableV2 with configurable settings and live preview',
            icon: <Table2 className="size-8 text-indigo-600" />,
            path: '/console/$orgId/component-showcase/table-showcase',
        },
        {
            title: 'Button showcase',
            description: 'Comprehensive guide to all Button components and variants used in the application',
            icon: <LayoutGrid className="size-8 text-blue-600" />,
            path: '/console/$orgId/component-showcase/button-variants',
        },
        {
            title: 'Fit on Page',
            description: 'Test page for layout and responsive design',
            icon: <FileText className="size-8 text-purple-600" />,
            path: '/console/$orgId/component-showcase/fit-on-page',
        },
        {
            title: 'AI Chat Test',
            description: 'Test the General AI Chatbot tools and queries with predefined test scenarios',
            icon: <Bot className="size-8 text-pink-600" />,
            path: '/console/$orgId/component-showcase/ai-chat-test',
        },
        {
            title: 'TipTap Editor',
            description: 'Test the new TipTap-based rich text editor with mentions, images, and formatting',
            icon: <PenLine className="size-8 text-teal-600" />,
            path: '/console/$orgId/component-showcase/tiptap-test',
        },
    ] satisfies { title: string; description: string; icon: React.ReactNode; path: FileRouteTypes['to'] }[];

    return (
        <PageController route={Route}>
            <div className="container mx-auto max-w-6xl p-6">
                <div className="mb-8">
                    <Title level={2} className="flex items-center">
                        <LayoutGrid className="mr-3" />
                        Component Showcase
                    </Title>
                    <Paragraph className="mt-2 text-base text-muted-foreground">
                        Browse UI component demos and development testing tools.
                    </Paragraph>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {showcasePages.map((item) => (
                        <Card
                            key={item.path}
                            hoverable
                            className="cursor-pointer transition-shadow"
                            onClick={() => navigate({ to: item.path, params: { orgId } })}
                        >
                            <div className="flex flex-col items-center p-4 text-center">
                                <div className="mb-4">{item.icon}</div>
                                <Title level={4} className="mb-2">
                                    {item.title}
                                </Title>
                                <Paragraph className="mb-0 text-muted-foreground text-sm">{item.description}</Paragraph>
                            </div>
                        </Card>
                    ))}
                </div>
            </div>
        </PageController>
    );
}
